import { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, Search, Copy } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import autoUploadsAPI from '@/api/auto-uploads.api';
import { FILE_STATUSES, METADATA_FIELDS } from './constants';
import { getMetadataBadgeProps } from './utils';
import { MetadataFieldsSelector } from '../metadata-fields-selector';
import { formatNumber } from 'shared/utils';

export function MetadataExtractionDialog({ open, onClose, uploadData }) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [selectedFields, setSelectedFields] = useState(new Set(METADATA_FIELDS.map(f => f.id)));

  // Get eligible files (files with knowledge item IDs)
  const eligibleFiles = useMemo(() => {
    if (!uploadData?.files) return [];
    return uploadData.files.filter(file => file.knowledgeItemId);
  }, [uploadData]);

  // Filter files based on search term
  const filteredFiles = useMemo(() => {
    if (!searchTerm) return eligibleFiles;
    const term = searchTerm.toLowerCase();
    return eligibleFiles.filter(
      file =>
        file.fileName.toLowerCase().includes(term) || file.c8Path?.toLowerCase().includes(term),
    );
  }, [eligibleFiles, searchTerm]);

  // Initialize selected items when dialog opens
  useEffect(() => {
    if (open && eligibleFiles.length > 0) {
      setSelectedItems(new Set(eligibleFiles.map(f => f.knowledgeItemId)));
    }
  }, [open, eligibleFiles]);

  const handleItemToggle = (itemId, checked) => {
    const newSelected = new Set(selectedItems);
    if (checked) {
      newSelected.add(itemId);
    } else {
      newSelected.delete(itemId);
    }
    setSelectedItems(newSelected);
  };

  const handleSelectAllItems = () => {
    setSelectedItems(new Set(filteredFiles.map(f => f.knowledgeItemId)));
  };

  const handleDeselectAllItems = () => {
    setSelectedItems(new Set());
  };

  const handleQueueExtraction = async () => {
    if (selectedItems.size === 0) {
      toast({
        title: 'Error',
        description: 'Please select at least one knowledge item',
        variant: 'destructive',
      });
      return;
    }

    if (selectedFields.size === 0) {
      toast({
        title: 'Error',
        description: 'Please select at least one metadata field',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    try {
      // If extraction was previously skipped, enable it on the upload first
      if (uploadData?.skipMetadataExtraction && uploadData?._id) {
        const enableResponse = await autoUploadsAPI.enableMetadataExtraction(uploadData._id);
        if (enableResponse.error) {
          throw new Error(enableResponse.error.message || 'Failed to enable metadata extraction');
        }
      }

      const response = await autoUploadsAPI.queueMetadataExtraction({
        knowledgeItemIds: Array.from(selectedItems),
        fields: Array.from(selectedFields),
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to queue metadata extraction');
      }

      const { requestedItems, nonExistentIds } = response.data;

      let message = `Metadata extraction working in the background for ${formatNumber(requestedItems)} item${
        requestedItems !== 1 ? 's' : ''
      }. To see individual progress, visit the item's page.`;

      if (nonExistentIds && nonExistentIds.length > 0) {
        message += ` Note: ${formatNumber(nonExistentIds.length)} item${
          nonExistentIds.length !== 1 ? 's' : ''
        } not found.`;
      }

      toast({
        title: 'Metadata Extraction Started',
        description: message,
      });

      onClose();
    } catch (error) {
      console.error('Error queuing metadata extraction:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to queue metadata extraction',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!uploadData) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Queue Metadata Extraction</DialogTitle>
          <DialogDescription>
            Select knowledge items and fields for metadata extraction
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-6 overflow-hidden">
          {/* Knowledge Items Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base">
                Knowledge Items ({selectedItems.size}/{eligibleFiles.length})
              </Label>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={handleSelectAllItems}>
                  Select All
                </Button>
                <Button size="sm" variant="outline" onClick={handleDeselectAllItems}>
                  Deselect All
                </Button>
              </div>
            </div>

            {eligibleFiles.length > 5 && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  type="text"
                  placeholder="Search files..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            )}

            <ScrollArea className="h-48 w-full rounded-md border p-4">
              {filteredFiles.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {searchTerm ? 'No files match your search' : 'No eligible files found'}
                </p>
              ) : (
                <div className="space-y-2">
                  {filteredFiles.map(file => (
                    <div
                      key={file.id}
                      className="flex items-start space-x-3 py-2 hover:bg-accent/50 rounded-md px-2">
                      <Checkbox
                        id={file.knowledgeItemId}
                        checked={selectedItems.has(file.knowledgeItemId)}
                        onCheckedChange={checked => handleItemToggle(file.knowledgeItemId, checked)}
                        className="mt-1"
                      />
                      <label
                        htmlFor={file.knowledgeItemId}
                        className="flex-1 cursor-pointer space-y-1">
                        <div className="font-medium text-sm">{file.fileName}</div>
                        {file.c8Path && (
                          <div className="text-xs text-muted-foreground">{file.c8Path}</div>
                        )}
                      </label>
                      {file.status === FILE_STATUSES.SUCCESS ? (
                        (() => {
                          const badgeProps = getMetadataBadgeProps(file);
                          return (
                            <Badge
                              variant={badgeProps.variant}
                              className={cn(badgeProps.className, 'shrink-0')}>
                              {badgeProps.text}
                            </Badge>
                          );
                        })()
                      ) : file.status === FILE_STATUSES.DUPLICATED ? (
                        <Badge
                          variant="secondary"
                          className="text-xs pointer-events-none bg-orange-100 text-orange-800 border-orange-200 shrink-0"
                          title="This file is identical to an existing file">
                          <Copy className="w-3 h-3 mr-1" />
                          Duplicate
                        </Badge>
                      ) : (
                        <Badge variant="default" className="text-xs pointer-events-none shrink-0">
                          Has Item
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          <MetadataFieldsSelector
            selectedFields={selectedFields}
            onFieldsChange={setSelectedFields}
            columns={2}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleQueueExtraction}
            disabled={selectedItems.size === 0 || selectedFields.size === 0 || isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Queuing...
              </>
            ) : (
              `Queue Extraction (${selectedItems.size} item${selectedItems.size !== 1 ? 's' : ''})`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
